import { useState } from 'react'
import { useUIStore } from '../store/uiStore.js'
import { useAuth } from '../hooks/useAuth.js'
import { colorFor } from '../data/userColors.js'

/**
 * UserChip — "who am I" picker for attribution.
 * Shows the current user's name as a pill. Click to switch.
 */

const TEAM = [
  'Ricky',
  'Zoe',
  'Christina',
  'Katie',
  'Margo',
  'Emily',
  'Sandra',
  'Sergina',
  'Jonathan',
  'Chris',
  'Harold',
  'Jessie',
  'Christine',
  'Braden',
  'Colin',
  'Jane',
]

export default function UserChip() {
  const currentUser = useUIStore((s) => s.currentUser)
  const setCurrentUser = useUIStore((s) => s.setCurrentUser)
  const { authenticated, email } = useAuth()
  const [open, setOpen] = useState(false)

  // In production the email-based identity is authoritative — we must NOT
  // let someone click to become someone else, since that would corrupt
  // attribution/DMs/presence. In local dev the /whoami endpoint returns
  // authenticated=false, and the picker is usable for testing.
  const locked = authenticated

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {currentUser ? (
        locked ? (
          <span
            title={email ? `Signed in as ${email}` : 'Signed in'}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid var(--border)',
              borderRadius: 99,
              padding: '5px 12px',
              fontSize: 11,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              userSelect: 'none',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: colorFor(currentUser),
                color: '#fff',
                fontSize: 9,
                fontWeight: 800,
                lineHeight: '16px',
                textAlign: 'center',
              }}
            >
              {currentUser[0]}
            </span>
            {currentUser}
          </span>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen((x) => !x)
            }}
            title="Click to change who you are (dev mode)"
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid var(--border)',
              borderRadius: 99,
              padding: '5px 12px',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: colorFor(currentUser),
                color: '#fff',
                fontSize: 9,
                fontWeight: 800,
                lineHeight: '16px',
                textAlign: 'center',
              }}
            >
              {currentUser[0]}
            </span>
            {currentUser}
            <span style={{ color: 'var(--muted-light)', fontSize: 9 }}>▼</span>
          </button>
        )
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((x) => !x)
          }}
          style={{
            background: '#fef3c7',
            color: '#92400e',
            border: '1px dashed #fde68a',
            borderRadius: 99,
            padding: '5px 12px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Set your name
        </button>
      )}

      {open && !locked && (
        <UserPicker
          onPick={(name) => {
            setCurrentUser(name)
            setOpen(false)
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  )
}

function UserPicker({ onPick, onClose }) {
  return (
    <>
      {/* Click-outside overlay — LOW z-index so other page elements
          (countdown, nav, etc.) aren't covered. Only sits below the popup. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 500,
          background: 'transparent',
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          right: 0,
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          padding: 6,
          minWidth: 150,
          maxHeight: 320,
          overflowY: 'auto',
          zIndex: 501,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            padding: '4px 8px 6px',
          }}
        >
          Who are you?
        </div>
        {TEAM.map((name) => (
          <button
            key={name}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPick(name)
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              border: 'none',
              background: 'transparent',
              padding: '6px 8px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <span
              style={{
                display: 'inline-block',
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: colorFor(name),
                color: '#fff',
                fontSize: 10,
                fontWeight: 800,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              {name[0]}
            </span>
            {name}
          </button>
        ))}
      </div>
    </>
  )
}

